import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { finalize } from 'rxjs';

import { MessageComponent } from 'src/app/components/message/message.component';
import { ApiResponses, BackendService } from 'src/app/services/backend.service';
import { NotificationService } from 'src/app/services/notification.service';
import { components } from 'src/app/shared/types/api.type';

type SurveyForm = ApiResponses['survey/form'];
type SurveyQuestion = SurveyForm['questions'][number];

export const SURVEY_NOTIFICATION_TYPE_KEY = 'survey';


/**
 * A survey delivered through the bell (SHIP-770). The recipient answers every question once; the
 * results are only visible in the survey portal, never here.
 */
@Component({
  selector: 'app-survey-notification-panel',
  templateUrl: './survey-notification-panel.component.html',
  styleUrls: ['./survey-notification-panel.component.scss'],
  imports: [
    DatePipe,
    FormsModule,
    TranslocoModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatRadioModule,
    MessageComponent
  ]
})
export class SurveyNotificationPanelComponent implements OnInit {
  /** The notification that delivered the survey; `metadata.surveyId` points at it */
  @Input({ required: true }) notification!: components['schemas']['InAppNotification'];
  /** Emitted once the answers are saved */
  @Output() readonly answered = new EventEmitter<void>();

  survey?: SurveyForm;
  loadFailed = false;
  submitting = false;
  /** Chosen option ids per question id */
  selection: Record<number, number[]> = {};

  constructor(
    private readonly backendService: BackendService,
    private readonly notify: NotificationService,
    private readonly translate: TranslocoService
  ) {}

  ngOnInit(): void {
    const { surveyId } = (this.notification.metadata ?? {}) as { surveyId?: number };
    if (!surveyId) {
      this.loadFailed = true;
      return;
    }

    this.backendService.get<'survey/form'>('surveys/{id}/form', { path: { id: surveyId } }).subscribe({
      next: (survey) => {
        this.survey = survey;
        this.selection = Object.fromEntries(survey.questions.map((question) => [question.id, []]));
      },
      error: () => (this.loadFailed = true)
    });
  }

  get canAnswer() {
    return this.survey?.state === 'open';
  }

  get isComplete() {
    return !!this.survey?.questions.every((question) => this.selection[question.id]?.length);
  }

  selectSingle(question: SurveyQuestion, optionId: number) {
    this.selection[question.id] = [optionId];
  }

  toggleMultiple(question: SurveyQuestion, optionId: number, checked: boolean) {
    const current = this.selection[question.id] ?? [];
    this.selection[question.id] = checked? [...current, optionId]: current.filter((id) => id !== optionId);
  }

  isSelected(question: SurveyQuestion, optionId: number) {
    return this.selection[question.id]?.includes(optionId) ?? false;
  }

  submit() {
    if (!this.survey || !this.canAnswer || !this.isComplete || this.submitting) return;

    this.submitting = true;
    this.backendService
      .post<'survey/submit'>('surveys/{id}/responses', {
        path: { id: this.survey.id },
        body: { answers: this.survey.questions.map((question) => ({ questionId: question.id, optionIds: this.selection[question.id] })) }
      })
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe((survey) => {
        this.survey = survey;
        this.notify.success(this.translate.translate('surveys.form.submitted'));
        this.answered.emit();
      });
  }
}
